import type { SupportCenterAssistanceRef } from './supportCenterAssistances'
import type { SupportCenterFormKind } from './supportCenterSpecialties'

export type { SupportCenterFormKind }
export { SUPPORT_CENTER_FORM_KIND_LABELS } from './supportCenterSpecialties'

/** Nutricional, fitness, jurídico e assistência social: triagem enxuta (origem, demanda e um texto). */
export function isReducedTriageForm(
  formKind: SupportCenterFormKind | null | undefined
): formKind is 'nutricional' | 'fitness' | 'juridico' | 'social' {
  return (
    formKind === 'nutricional' ||
    formKind === 'fitness' ||
    formKind === 'juridico' ||
    formKind === 'social'
  )
}

/** Rótulo do texto livre da triagem, conforme o formulário. */
export function triageMainReasonLabel(
  formKind: SupportCenterFormKind | null | undefined
): string {
  if (formKind === 'nutricional' || formKind === 'fitness') {
    return 'Motivo/objetivo com a assistência'
  }
  if (formKind === 'juridico' || formKind === 'social') return 'Motivo do acionamento'
  return 'Motivo principal da procura'
}

/** Rótulo do texto único do atendimento reduzido. */
export function attendanceNotesLabel(
  formKind: SupportCenterFormKind | null | undefined
): string {
  if (formKind === 'juridico' || formKind === 'social') return 'Orientação repassada'
  return 'Observações do atendimento'
}

export function reducedTriageRequiredMessage(formKind: SupportCenterFormKind): string {
  if (formKind === 'nutricional' || formKind === 'fitness') {
    return 'Informe o motivo/objetivo com a assistência.'
  }
  return 'Informe o motivo do acionamento.'
}

export type SupportCenterTriageStatus =
  | 'pendente'
  | 'concluida'
  | 'reprovado'
  | 'cancelado'
  | 'nao_realizado'

export type SupportCenterAppointmentStatus =
  | 'marcado'
  | 'reagendado'
  | 'cancelado'
  | 'realizado'
  | 'nao_realizado'

export type SupportCenterTriageRiskAnswer =
  | 'yes'
  | 'no'
  | 'refused'
  | 'unable_to_assess'
  | ''

export type SupportCenterTriageSufferingIntensity =
  | 'mild'
  | 'moderate'
  | 'intense'
  | 'unable_to_assess'
  | ''

export type SupportCenterTriageDemandNature =
  | 'situational'
  | 'recurring'
  | 'chronic'
  | 'unable_to_assess'
  | ''

/** Catalog option: origem do encaminhamento */
export interface SupportCenterReferralOrigin {
  id: string
  name: string
  requires_notes: boolean
  created_at: string
  updated_at: string
  deleted_at: string | null
  deleted_by: string | null
  deleted_reason: string | null
}

/** Catalog option: tipo de demanda percebida */
export interface SupportCenterDemandType {
  id: string
  name: string
  requires_notes: boolean
  assistances: SupportCenterAssistanceRef[]
  created_at: string
  updated_at: string
  deleted_at: string | null
  deleted_by: string | null
  deleted_reason: string | null
}

export type SupportCenterDemandSelectionItem = {
  demand_type_id: string
  notes: string
}

export type SupportCenterSelectedDemandType = Pick<
  SupportCenterDemandType,
  'id' | 'name' | 'requires_notes'
> & {
  notes: string | null
}

export type SupportCenterTriageCatalogInput = {
  id?: string
  name: string
  requires_notes: boolean
  /** Required for demand types */
  assistance_ids?: string[]
}

export interface SupportCenterTriageObjectiveData {
  assistance_objective: string
}

export interface SupportCenterTriageJuridicalData {
  activation_reason: string
}

export const EMPTY_TRIAGE_OBJECTIVE: SupportCenterTriageObjectiveData = {
  assistance_objective: '',
}

export const EMPTY_TRIAGE_JURIDICAL: SupportCenterTriageJuridicalData = {
  activation_reason: '',
}

/** Texto da triagem enxuta (nutricional, fitness, jurídico ou assistência social). */
export function reducedTriageText(
  form: Pick<SupportCenterTriageFormData, 'nutritional' | 'fitness' | 'juridical' | 'social'>,
  formKind: SupportCenterFormKind
): string {
  if (formKind === 'juridico') return form.juridical.activation_reason
  if (formKind === 'social') return form.social.activation_reason
  if (formKind === 'fitness') return form.fitness.assistance_objective
  if (formKind === 'nutricional') return form.nutritional.assistance_objective
  return ''
}

export function withReducedTriageText(
  form: SupportCenterTriageFormData,
  formKind: SupportCenterFormKind,
  value: string
): SupportCenterTriageFormData {
  if (formKind === 'juridico') {
    return { ...form, juridical: { activation_reason: value } }
  }
  if (formKind === 'social') {
    return { ...form, social: { activation_reason: value } }
  }
  if (formKind === 'fitness') {
    return { ...form, fitness: { assistance_objective: value } }
  }
  if (formKind === 'nutricional') {
    return { ...form, nutritional: { assistance_objective: value } }
  }
  return form
}

export interface SupportCenterTriagePsychologicalData {
  suffering_intensity: SupportCenterTriageSufferingIntensity
  demand_nature: SupportCenterTriageDemandNature
  suicidal_ideation: SupportCenterTriageRiskAnswer
  self_harm: SupportCenterTriageRiskAnswer
  aggression_risk: SupportCenterTriageRiskAnswer
  domestic_violence: SupportCenterTriageRiskAnswer
  observations: string
}

export interface SupportCenterTriageFormData {
  triage_date: string
  intern_id: string
  emergency_contact_phone: string
  emergency_contact_relation: string
  referral_origin_id: string
  referral_origin_other: string
  main_reason: string
  availability: string
  demand_selections: SupportCenterDemandSelectionItem[]
  psychological: SupportCenterTriagePsychologicalData
  nutritional: SupportCenterTriageObjectiveData
  fitness: SupportCenterTriageObjectiveData
  juridical: SupportCenterTriageJuridicalData
  social: SupportCenterTriageJuridicalData
}

export const EMPTY_TRIAGE_PSYCHOLOGICAL: SupportCenterTriagePsychologicalData = {
  suffering_intensity: '',
  demand_nature: '',
  suicidal_ideation: '',
  self_harm: '',
  aggression_risk: '',
  domestic_violence: '',
  observations: '',
}

export const EMPTY_TRIAGE_FORM_DATA: SupportCenterTriageFormData = {
  triage_date: '',
  intern_id: '',
  emergency_contact_phone: '',
  emergency_contact_relation: '',
  referral_origin_id: '',
  referral_origin_other: '',
  main_reason: '',
  availability: '',
  demand_selections: [],
  psychological: { ...EMPTY_TRIAGE_PSYCHOLOGICAL },
  nutritional: { ...EMPTY_TRIAGE_OBJECTIVE },
  fitness: { ...EMPTY_TRIAGE_OBJECTIVE },
  juridical: { ...EMPTY_TRIAGE_JURIDICAL },
  social: { ...EMPTY_TRIAGE_JURIDICAL },
}

export interface SupportCenterPeopleRef {
  id: number
  name: string | null
  tax_id_number: string | null
  /** Data de nascimento em people (fonte da idade na triagem) */
  captured_birth_date: string | null
}

export interface SupportCenterTriagePsychological {
  id: string
  suffering_intensity: SupportCenterTriageSufferingIntensity | null
  demand_nature: SupportCenterTriageDemandNature | null
  suicidal_ideation: SupportCenterTriageRiskAnswer | null
  self_harm: SupportCenterTriageRiskAnswer | null
  aggression_risk: SupportCenterTriageRiskAnswer | null
  domestic_violence: SupportCenterTriageRiskAnswer | null
  observations: string | null
  created_at: string
  updated_at: string
}

export interface SupportCenterTriageObjectiveDetail {
  id: string
  assistance_objective: string | null
  created_at: string
  updated_at: string
}

export interface SupportCenterTriageJuridicalDetail {
  id: string
  activation_reason: string | null
  created_at: string
  updated_at: string
}

export interface SupportCenterTriage {
  id: string
  status: SupportCenterTriageStatus
  triage_date: string | null
  intern_id: string | null
  emergency_contact_phone: string | null
  emergency_contact_relation: string | null
  referral_origin_id: string | null
  referral_origin_other: string | null
  main_reason: string | null
  availability: string | null
  /** Key do formulário específico (support_center_forms). */
  form_key: string | null
  /** Motivo cadastrado (kind=triagem) na reprovação/cancelamento. */
  cancel_reason_id: string | null
  cancel_reason?: {
    id: string
    name: string
    description: string
  } | null
  triaged_at: string | null
  triaged_by: string | null
  created_at: string
  updated_at: string
  deleted_at: string | null
  deleted_by: string | null
  deleted_reason: string | null
  intern?: { id: string; name: string | null } | null
  /** Quem concluiu/reprovou a triagem (profiles via triaged_by). */
  triaged_by_user?: { id: string; name: string | null } | null
  referral_origin?: Pick<SupportCenterReferralOrigin, 'id' | 'name' | 'requires_notes'> | null
  demand_types?: SupportCenterSelectedDemandType[]
  psychological?: SupportCenterTriagePsychological | null
  nutritional?: SupportCenterTriageObjectiveDetail | null
  fitness?: SupportCenterTriageObjectiveDetail | null
  juridical?: SupportCenterTriageJuridicalDetail | null
  social?: SupportCenterTriageJuridicalDetail | null
}

export interface SupportCenterAppointment {
  id: string
  call_link: string | null
  /** URL encurtada da reunião. Usada ao copiar o link no calendário. */
  call_link_short: string | null
  holder_cpf: string
  patient_cpf: string
  relationship_to_holder: string | null
  internal_emails: string[]
  /** Resolvido via people_contacts (não é coluna do agendamento). */
  client_emails: string[]
  /** Resolvido via people_contacts (não é coluna do agendamento). */
  patient_phone: string | null
  phone_contact_id?: string | null
  email_contact_ids?: string[]
  specialization_id: string
  assistance_id?: string | null
  /** Canal de origem: whatsapp, telefone, videochamada ou whatsapp-bot. */
  channel?: 'whatsapp' | 'telefone' | 'videochamada' | 'whatsapp-bot' | null
  /** Apólice gravada no agendamento (policy_code). */
  policy_code?: number | null
  /** Subgrupo gravado no agendamento (agreement_code). */
  agreement_code?: number | null
  /** Fornecedor de assistência gravado no agendamento. */
  assistance_provider_code?: number | null
  professional_user_id?: string | null
  /** Id do evento no Microsoft Graph/Outlook (único quando preenchido). */
  outlook_event_id: string | null
  /**
   * Triagem do caso (patient_cpf + assistance_id via support_center_assistance_triages).
   * Não é coluna de support_center_appointments.
   */
  triage_id: string | null
  scheduled_at: string
  confirmed_at: string | null
  confirmation_requested_at?: string | null
  /** Início do atendimento (primeiro acesso ao formulário). */
  attendance_started_at: string | null
  /** Finalização do atendimento (status realizado). */
  attendance_completed_at: string | null
  status: SupportCenterAppointmentStatus
  created_at: string
  updated_at: string
  deleted_at: string | null
  deleted_by: string | null
  cancel_reason_id: string | null
  /** Canal usado no cancelamento (coluna cancellation_channel). */
  cancellation_channel?: 'whatsapp' | 'telefone' | 'videochamada' | 'whatsapp-bot' | null
  cancel_reason?: {
    id: string
    name: string
    description: string
  } | null
  specialization?: {
    id: string
    name: string
  } | null
  assistance?: {
    id: string
    name: string
    code: string
    form_kind: SupportCenterFormKind | null
    specific_form_key: string | null
    specific_form: {
      form_key: string
      name: string
      table_name: string
      triage_table_name: string
    } | null
  } | null
  professional?: {
    id: string
    name: string | null
    email: string | null
  } | null
  holder_people?: SupportCenterPeopleRef | null
  patient_people?: SupportCenterPeopleRef | null
}

export interface SupportCenterTriageListItem extends SupportCenterTriage {
  appointment: SupportCenterAppointment | null
}

export type SupportCenterTriageFilters = {
  status?: SupportCenterTriageStatus | 'all'
  /**
   * Tem prioridade sobre `status`.
   * Vazio = Pendente (padrão da tela). Todos os status marcados = sem filtro.
   */
  statuses?: SupportCenterTriageStatus[]
  specialization_id?: string
  /** Filtro da tela: especialidades marcadas. Vazio = todas. */
  specialization_filter_ids?: string[]
  /** Quando informado, só retorna casos desses profissionais */
  specialization_ids?: string[]
  /**
   * Escopo restrito (sem view_values): e-mail do profissional/atendente
   * (match em appointment.internal_emails), igual ao calendário.
   */
  attendant_email?: string
  /** Profissional da consulta (appointment.professional_user_id). */
  professional_user_id?: string
  /** Filtro da tela: profissionais marcados. Vazio = todos. */
  professional_user_ids?: string[]
  /** Quem concluiu/reprovou a triagem (triaged_by). */
  triaged_by?: string
  /** Filtro da tela: quem triou. Vazio = todos. */
  triaged_by_ids?: string[]
  date_from?: string
  date_to?: string
  cpf?: string
  phone?: string
}

export function triageRowToFormData(
  row: Pick<
    SupportCenterTriage,
    | 'triage_date'
    | 'intern_id'
    | 'emergency_contact_phone'
    | 'emergency_contact_relation'
    | 'referral_origin_id'
    | 'referral_origin_other'
    | 'main_reason'
    | 'availability'
    | 'demand_types'
    | 'psychological'
    | 'nutritional'
    | 'fitness'
    | 'juridical'
    | 'social'
  >
): SupportCenterTriageFormData {
  const psy = row.psychological
  return {
    triage_date: row.triage_date ?? '',
    intern_id: row.intern_id ?? '',
    emergency_contact_phone: row.emergency_contact_phone ?? '',
    emergency_contact_relation: row.emergency_contact_relation ?? '',
    referral_origin_id: row.referral_origin_id ?? '',
    referral_origin_other: row.referral_origin_other ?? '',
    main_reason: row.main_reason ?? '',
    availability: row.availability ?? '',
    demand_selections: (row.demand_types ?? []).map((item) => ({
      demand_type_id: item.id,
      notes: item.notes ?? '',
    })),
    psychological: {
      suffering_intensity: psy?.suffering_intensity ?? '',
      demand_nature: psy?.demand_nature ?? '',
      suicidal_ideation: psy?.suicidal_ideation ?? '',
      self_harm: psy?.self_harm ?? '',
      aggression_risk: psy?.aggression_risk ?? '',
      domestic_violence: psy?.domestic_violence ?? '',
      observations: psy?.observations ?? '',
    },
    nutritional: {
      assistance_objective: row.nutritional?.assistance_objective ?? '',
    },
    fitness: {
      assistance_objective: row.fitness?.assistance_objective ?? '',
    },
    juridical: {
      activation_reason: row.juridical?.activation_reason ?? '',
    },
    social: {
      activation_reason: row.social?.activation_reason ?? '',
    },
  }
}

/** Exibe ano de nascimento e idade a partir de people.captured_birth_date */
export function formatBirthYearAndAge(
  birthDateRaw: string | null | undefined,
  asOfIsoDate?: string | null
): string {
  if (!birthDateRaw?.trim()) return '—'
  const birth = parseFlexibleDate(birthDateRaw)
  if (!birth) return '—'

  const asOf = asOfIsoDate ? parseFlexibleDate(asOfIsoDate) : new Date()
  const ref = asOf ?? new Date()
  let age = ref.getFullYear() - birth.getFullYear()
  const monthDiff = ref.getMonth() - birth.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && ref.getDate() < birth.getDate())) {
    age -= 1
  }
  if (age < 0) age = 0

  const year = birth.getFullYear()
  return `${year} (${age} ${age === 1 ? 'ano' : 'anos'})`
}

function parseFlexibleDate(value: string): Date | null {
  const trimmed = value.trim()
  if (!trimmed) return null

  // yyyy-mm-dd or ISO
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    const d = new Date(`${trimmed.slice(0, 10)}T12:00:00`)
    return Number.isNaN(d.getTime()) ? null : d
  }

  // dd/mm/yyyy or dd-mm-yyyy
  const br = trimmed.match(/^(\d{2})[/-](\d{2})[/-](\d{4})/)
  if (br) {
    const d = new Date(`${br[3]}-${br[2]}-${br[1]}T12:00:00`)
    return Number.isNaN(d.getTime()) ? null : d
  }

  const d = new Date(trimmed)
  return Number.isNaN(d.getTime()) ? null : d
}

export type SupportCenterTriageCompleteInput = {
  id: string
  form_kind: SupportCenterFormKind
  form: SupportCenterTriageFormData
  actor_id: string
  /** Agendamento de contexto (calendário); usado na janela de horário. */
  appointment_id?: string | null
}

export type SupportCenterTriageSaveFormInput = Omit<SupportCenterTriageCompleteInput, 'actor_id'>

export type SupportCenterTriageRejectInput = {
  id: string
  /** Agendamento de contexto a cancelar (só este do caso). */
  appointment_id: string
  /** UUID em support_center_cancel_reasons (kind=triagem); grava na triagem e no agendamento de contexto. */
  cancel_reason_id: string
  actor_id: string
  /** Canal do cancelamento do agendamento de contexto (POST /agendamentos/{id}/cancelar). */
  cancellation_channel: 'whatsapp' | 'telefone' | 'videochamada'
}

export type SupportCenterTriageCancelInput = SupportCenterTriageRejectInput

export const TRIAGE_STATUS_LABELS: Record<SupportCenterTriageStatus, string> = {
  pendente: 'Pendente',
  concluida: 'Concluída',
  reprovado: 'Reprovado',
  cancelado: 'Cancelado',
  nao_realizado: 'Não Realizado',
}

export const TRIAGE_RISK_OPTIONS: Array<{
  value: Exclude<SupportCenterTriageRiskAnswer, ''>
  label: string
}> = [
  { value: 'yes', label: 'Sim' },
  { value: 'no', label: 'Não' },
  { value: 'refused', label: 'Não quis falar' },
  { value: 'unable_to_assess', label: 'Não foi possível avaliar' },
]

export const TRIAGE_SUFFERING_OPTIONS: Array<{
  value: Exclude<SupportCenterTriageSufferingIntensity, ''>
  label: string
}> = [
  { value: 'mild', label: 'Leve' },
  { value: 'moderate', label: 'Moderada' },
  { value: 'intense', label: 'Intensa' },
  { value: 'unable_to_assess', label: 'Não foi possível avaliar' },
]

export const TRIAGE_DEMAND_NATURE_OPTIONS: Array<{
  value: Exclude<SupportCenterTriageDemandNature, ''>
  label: string
}> = [
  { value: 'situational', label: 'Pontual / situacional' },
  { value: 'recurring', label: 'Recorrente' },
  { value: 'chronic', label: 'Crônica' },
  { value: 'unable_to_assess', label: 'Não foi possível avaliar' },
]
